import React, { useEffect } from 'react'
import { isMobile } from 'react-device-detect'

import { OrdersContext } from 'contexts/OrdersContext'
import {
  DcSwitch,
  PixelDiv,
  PixelFactoryContext,
  PixelFlexBox,
  PixelText,
  Section,
  SectionContent,
} from 'pixel-base'
import styled from 'styled-components'
import { del, get, post } from 'utils/api-routes.util'
import { IPixelFactory } from '../../../../../../decl.d'

export interface IEvent {
  id: number
  event_code: string
  event_name: string
  is_active: boolean
  template: string
  communication_type: string
  is_subscribed: boolean
}

export interface IModifyEvent {
  [key: string]: IEvent[]
}

export interface CreatedAt {
  date: Date
  timezone_type: number
  timezone: string
}

export interface IPlan {
  id: number
  service: Service
  plan_name: string
  plan_cost: number | null
  is_active: boolean
}

interface Service {
  id: number
  service_name: string
  service_description: string
  is_active: boolean
}

const EventSubscriptions: React.FC = () => {
  const { showSuccess, showFailure, showLoader, hideLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const { state } = React.useContext(OrdersContext)
  const [events, setEvents] = React.useState<IModifyEvent[]>([])
  const enrollConfigRef = React.useRef<HTMLElement>(null)
  const [autoNotify, setAutoNotify] = React.useState(false)

  useEffect(() => {
    if (events?.length === 0) getAllSmsEvents()
    getNotifyStatus()
  }, [])

  const getNotifyStatus = () => {
    showLoader(enrollConfigRef)
    get('/communication/wa/settings').subscribe((res: any) => {
      hideLoader(enrollConfigRef)
      if (res.response) {
        const notifyStatus = res.response.auto_notify_whatsapp ? true : false
        setAutoNotify(notifyStatus)
      } else if (res.response.error) {
        showFailure(
          res.response.error || 'Error in fetching Auto Notify Status',
        )
      } else {
        showFailure('Something went wrong')
      }
    })
  }

  const updateAutoNotify = (value) => {
    showLoader(enrollConfigRef)
    post('/communication/wa/config/auto-notify', {
      auto_notify_enrollment: value,
    }).subscribe((res: any) => {
      hideLoader(enrollConfigRef)
      if (res.response) {
        const notifyStatus = res.response.auto_notify_whatsapp ? true : false
        setAutoNotify(notifyStatus)
        showSuccess('Updated Successfully')
      } else if (res.response.error) {
        showFailure(
          res.response.error || 'Error in fetching Auto Notify Status',
        )
      } else {
        showFailure('Something went wrong')
      }
    })
    setAutoNotify(value)
  }

  const formatData = (data: IEvent[]): IModifyEvent[] => {
    const transformedData: IModifyEvent[] = []

    data.forEach((event) => {
      const eventCode = event?.event_code
      transformedData.push({ [eventCode]: [event] })
    })

    return transformedData
  }

  const getAllSmsEvents = () => {
    showLoader(enrollConfigRef)
    get('/communication/events?type=whatsapp').subscribe((res: any) => {
      hideLoader(enrollConfigRef)
      if (res.response.data) {
        setEvents(formatData(res.response.data))
      }
    })
  }

  const enrollEvent = (event: IEvent) => {
    showLoader(enrollConfigRef)
    if (event.is_subscribed) {
      post('/communication/events/subscribe', {
        event_id: event.event_id,
      }).subscribe((res: any) => {
        hideLoader(enrollConfigRef)
        if (res.response.data) {
          setEvents(formatData(res.response.data))
        } else {
          showFailure(res.response.error)
        }
      })
    } else {
      del(`/communication/events/${event.event_id}`).subscribe((res: any) => {
        hideLoader(enrollConfigRef)
        if (res.response.data) {
          setEvents(formatData(res.response.data))
        } else {
          showFailure(res.response.error)
        }
      })
    }
  }

  return (
    <PixelFlexBox>
      <Section style={{ padding: '0' }} isMobile={isMobile}>
        <SectionContent
          style={{ boxShadow: 'none', padding: '0' }}
          isMobile={isMobile}
          className="sms-settings"
          ref={enrollConfigRef}
        >
          <PixelDiv style={{ border: '1px solid #e6e6e6' }}>
            <PixelFlexBox
              style={{ width: '100%', borderBottom: '1px solid #e6e6e6' }}
              gap="15px"
              alignItems="center"
            >
              <PixelDiv style={{ height: '100%' }} width="auto">
                {' '}
                <PixelFlexBox
                  padding="10px"
                  style={{ maxWidth: '70px', minWidth: '70px' }}
                  justifyContent="flex-end"
                >
                  <DcSwitch
                    value={autoNotify}
                    onChange={(e) => updateAutoNotify(e.target.checked)}
                    checked={autoNotify}
                    label={autoNotify}
                  />
                </PixelFlexBox>
              </PixelDiv>
              <PixelDiv
                style={{
                  borderRight: '1px solid #e0e0e0',
                  height: '100%',
                  padding: '15px',
                }}
                width="20%"
              >
                <PixelText
                  textSize="20px"
                  style={{ fontWeight: 'bold' }}
                  customColor="#2f3c4d"
                  margin="0px 0px 0px 0px"
                >
                  Subscribe to Events
                </PixelText>
              </PixelDiv>
              <PixelDiv width="74%">
                <PixelText
                  textSize="20px"
                  style={{ fontWeight: 'bold' }}
                  customColor="#2f3c4d"
                  margin="0px 0px 0px 0px"
                >
                  Message
                </PixelText>
              </PixelDiv>
            </PixelFlexBox>
            <Events className={!autoNotify ? 'disabled' : ''}>
              {events.length &&
                events.map((event: IModifyEvent, i: number) => (
                  <PixelFlexBox
                    alignItems="center"
                    gap="15px"
                    style={{
                      borderBottom: '1px solid #e6e6e6',
                    }}
                    key={i}
                    height={'auto'}
                  >
                    <PixelDiv
                      style={{
                        height: '100%',
                      }}
                      width="auto"
                    >
                      {event[Object.keys(event)[0]].map(
                        (singleEvent: IEvent, i: number) => (
                          <PixelFlexBox
                            key={i}
                            padding="10px "
                            justifyContent="flex-end"
                            style={{ maxWidth: '70px', minWidth: '70px' }}
                          >
                            <DcSwitch
                              className={''}
                              value={singleEvent.is_subscribed}
                              disabled={
                                singleEvent.event_code === 'order_awaiting' ||
                                singleEvent.event_code === 'order_refused'
                              }
                              onChange={(e) => {
                                enrollEvent({
                                  is_subscribed: e.target.checked,
                                  event_id: singleEvent.id,
                                })
                              }}
                              checked={singleEvent.is_subscribed}
                              label={singleEvent.is_subscribed}
                            />
                          </PixelFlexBox>
                        ),
                      )}
                    </PixelDiv>
                    <PixelDiv
                      style={{
                        borderRight: '1px solid #e0e0e0',
                        height: '100%',
                        padding: '17px',
                      }}
                      width="20%"
                    >
                      <PixelText textSize="19px" style={{ fontWeight: '700' }}>
                        {Object.keys(event)[0].replace('_', ' ')}
                      </PixelText>
                    </PixelDiv>
                    <PixelDiv width="74%">
                      {event[Object.keys(event)[0]].map(
                        (singleEvent: IEvent, i: number) => (
                          <PixelFlexBox
                            key={i}
                            flexDirection="column"
                            gap="10px"
                          >
                            <PixelText
                              textSize="17px"
                              customColor="#545454;"
                              style={{ fontWeight: '400' }}
                              key={singleEvent.id}
                            >
                              <span style={{ color: 'black', fontWeight: 500 }}>
                                Message:{' '}
                              </span>{' '}
                              {singleEvent.template?.replace(
                                '{brand_name}',
                                `${state.tenantInfo.business_name}`,
                              )}
                            </PixelText>
                          </PixelFlexBox>
                        ),
                      )}
                    </PixelDiv>
                  </PixelFlexBox>
                ))}
            </Events>
          </PixelDiv>
        </SectionContent>
      </Section>
    </PixelFlexBox>
  )
}

const Events = styled.div`
  position: relative;
  &.disabled {
    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #f2f2f2;
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`

export default EventSubscriptions
